import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import { formatPrice } from '../utils/formatters';

const DemoPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);

  const demoSteps = [
    {
      id: 'welcome',
      title: 'Welcome to DomainFlip!',
      description: "Let's take a quick tour of the platform",
      component: 'welcome',
      tips: [
        'This demo takes about 5 minutes',
        'You can skip ahead at any time',
        'No account needed to explore'
      ]
    },
    {
      id: 'browse',
      title: 'Browse Marketplace',
      description: 'Discover thousands of premium domains',
      component: 'marketplace',
      tips: [
        'Filter by price, TLD, or category',
        'Sort by trending or recently listed',
        'Save your favorites with one click'
      ]
    },
    {
      id: 'view-domain',
      title: 'View Domain Details',
      description: 'See detailed info and AI analysis',
      component: 'domain-detail',
      tips: [
        'AI valuations help you make smart decisions',
        'View price history and market trends',
        'Check SEO metrics and domain authority'
      ]
    },
    {
      id: 'purchase',
      title: 'Make a Purchase',
      description: 'Buy instantly or make an offer',
      component: 'purchase-flow',
      tips: [
        'Buy now for instant ownership',
        'Make an offer to negotiate price',
        'Propose a domain trade',
        'All transactions are secure with escrow'
      ]
    },
    {
      id: 'list',
      title: 'List Your Domain',
      description: 'Sell your domains in 3 easy steps',
      component: 'list-flow',
      tips: [
        'Set your price or accept offers',
        'AI suggests optimal pricing',
        'Get notified of all activity'
      ]
    },
    {
      id: 'portfolio',
      title: 'Manage Portfolio',
      description: 'Track all your domains and profits',
      component: 'portfolio',
      tips: [
        'See real-time portfolio value',
        'Track ROI on each domain',
        'Analyze your trading performance'
      ]
    },
    {
      id: 'compete',
      title: 'Climb the Leaderboard',
      description: 'Compete and earn prizes',
      component: 'leaderboard',
      tips: [
        'Monthly cash prizes for top traders',
        'Multiple categories to compete in',
        'Earn achievements and badges'
      ]
    },
    {
      id: 'complete',
      title: "You're Ready to Trade!",
      description: 'Start your domain trading journey',
      component: 'complete',
      tips: [
        'Create your free account',
        'Start with as little as $100',
        'Join our community of 10,000+ traders'
      ]
    }
  ];

  const currentStepData = demoSteps[currentStep];
  const progress = ((currentStep + 1) / demoSteps.length) * 100;

  const handleNext = () => {
    if (!completedSteps.includes(currentStep)) {
      setCompletedSteps([...completedSteps, currentStep]);
    }

    if (currentStep < demoSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setShowCelebration(true);
      setTimeout(() => {
        navigate('/marketplace');
      }, 3000);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    navigate('/marketplace');
  };

  const DemoStepContent = () => {
    switch (currentStepData.component) {
      case 'welcome':
        return (
          <div className="text-center py-12">
            <div className="text-8xl mb-8 animate-bounce">👋</div>
            <h2 className="text-4xl font-bold mb-4">Welcome to DomainFlip!</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              The modern way to trade domain names. Fast, social, and profitable.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <Card className="p-6 text-center">
                <div className="text-4xl mb-3">⚡</div>
                <h3 className="font-bold mb-2">Instant Trading</h3>
                <p className="text-sm text-gray-600">Buy and sell in seconds</p>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-4xl mb-3">🤖</div>
                <h3 className="font-bold mb-2">AI Valuations</h3>
                <p className="text-sm text-gray-600">Smart pricing insights</p>
              </Card>
              <Card className="p-6 text-center">
                <div className="text-4xl mb-3">🏆</div>
                <h3 className="font-bold mb-2">Compete & Win</h3>
                <p className="text-sm text-gray-600">Monthly cash prizes</p>
              </Card>
            </div>
          </div>
        );

      case 'marketplace':
        return (
          <div className="py-8">
            <div className="mb-6">
              <input
                type="text"
                placeholder="Search domains..."
                className="w-full px-4 py-3 border rounded-lg mb-4"
                disabled
              />
              <div className="flex gap-2">
                <Badge>Tech</Badge>
                <Badge>Crypto</Badge>
                <Badge>Finance</Badge>
                <Badge variant="warning">🔥 Trending</Badge>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {['TechStartup.io', 'AINews.com', 'CryptoHub.com'].map((domain, i) => (
                <Card key={i} hover className="p-6 cursor-pointer">
                  <h3 className="font-bold text-xl mb-2">{domain}</h3>
                  <p className="text-3xl font-bold text-blue-600 mb-4">
                    {formatPrice([5000, 12000, 8500][i])}
                  </p>
                  <div className="flex items-center justify-between text-sm text-gray-600">
                    <span>👁 1.2K views</span>
                    <span>❤️ 87 likes</span>
                  </div>
                  <Button fullWidth className="mt-4" size="sm">
                    View Details
                  </Button>
                </Card>
              ))}
            </div>
          </div>
        );

      case 'domain-detail':
        return (
          <div className="py-8">
            <h2 className="text-3xl font-bold mb-6">TechStartup.io</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <div className="flex justify-between mb-6">
                  <div>
                    <p className="text-sm text-gray-600">Current Price</p>
                    <p className="text-4xl font-bold">{formatPrice(5000)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">AI Valuation</p>
                    <p className="text-lg font-semibold text-blue-600">
                      {formatPrice(4500)} - {formatPrice(6200)}
                    </p>
                    <p className="text-xs text-gray-500">89% confidence</p>
                  </div>
                </div>
                <Button fullWidth size="lg" variant="success">
                  Buy Now for {formatPrice(5000)}
                </Button>
              </Card>
              <Card className="p-6">
                <h3 className="font-bold mb-4">🤖 AI Analysis</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Brandability', value: 5 },
                    { label: 'Memorability', value: 4 },
                    { label: 'TLD Premium', value: 4 }
                  ].map(item => (
                    <div key={item.label}>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm">{item.label}</span>
                        <span className="text-sm text-gray-600">{item.value}/5</span>
                      </div>
                      <div className="flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <div
                            key={i}
                            className={`h-2 flex-1 rounded ${
                              i < item.value ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        );

      case 'purchase-flow':
        return (
          <div className="max-w-2xl mx-auto py-12">
            <Card className="p-8">
              <h2 className="text-2xl font-bold text-center mb-6">
                Choose Your Trading Method
              </h2>
              <div className="space-y-4">
                <Card hover className="p-6 cursor-pointer border-2 border-transparent hover:border-blue-600">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">💰</div>
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">Buy Now</h3>
                      <p className="text-sm text-gray-600">
                        Instant purchase at listed price
                      </p>
                    </div>
                    <span className="text-2xl font-bold text-blue-600">
                      {formatPrice(5000)}
                    </span>
                  </div>
                </Card>
                <Card hover className="p-6 cursor-pointer border-2 border-transparent hover:border-blue-600">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">🎯</div>
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">Make an Offer</h3>
                      <p className="text-sm text-gray-600">
                        Negotiate the best price
                      </p>
                    </div>
                  </div>
                </Card>
                <Card hover className="p-6 cursor-pointer border-2 border-transparent hover:border-blue-600">
                  <div className="flex items-center gap-4">
                    <div className="text-4xl">🔄</div>
                    <div className="flex-1">
                      <h3 className="font-bold mb-1">Propose Trade</h3>
                      <p className="text-sm text-gray-600">
                        Swap with your own domain
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </Card>
          </div>
        );

      case 'list-flow':
        return (
          <div className="max-w-2xl mx-auto py-8">
            <Card className="p-8">
              <h2 className="text-2xl font-bold mb-6">List Your Domain</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Domain Name
                  </label>
                  <input
                    type="text"
                    placeholder="example.com"
                    className="w-full px-4 py-3 border rounded-lg"
                    disabled
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Listing Price
                  </label>
                  <input
                    type="text"
                    placeholder="$5,000"
                    className="w-full px-4 py-3 border rounded-lg"
                    disabled
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    💡 AI suggests: {formatPrice(4800)} - {formatPrice(6200)}
                  </p>
                </div>
                <div>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" disabled />
                    <span className="text-sm">Accept offers</span>
                  </label>
                  <label className="flex items-center gap-2 mt-2">
                    <input type="checkbox" disabled />
                    <span className="text-sm">Open to domain trades</span>
                  </label>
                </div>
                <Button fullWidth size="lg">
                  List Domain
                </Button>
              </div>
            </Card>
          </div>
        );

      case 'portfolio':
        return (
          <div className="py-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Total Value', value: formatPrice(24500) },
                { label: 'Total Domains', value: '8' },
                { label: 'Total Profit', value: formatPrice(5200) },
                { label: 'ROI', value: '+23%' }
              ].map((stat, i) => (
                <Card key={i} className="p-4 text-center">
                  <p className="text-2xl font-bold text-blue-600">{stat.value}</p>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                </Card>
              ))}
            </div>
            <Card className="p-6">
              <h3 className="font-bold mb-4">Your Domains</h3>
              <div className="space-y-3">
                {['MyStartup.com', 'TechVenture.io', 'AIRevolution.com'].map((domain, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="font-medium">{domain}</span>
                    <Badge variant="success">Listed</Badge>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        );

      case 'leaderboard':
        return (
          <div className="py-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mb-2">🏆 Leaderboard</h2>
              <p className="text-gray-600">Compete for monthly prizes</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {[
                { place: '🥇', prize: '$500', label: '1st Place' },
                { place: '🥈', prize: '$250', label: '2nd Place' },
                { place: '🥉', prize: '$100', label: '3rd Place' }
              ].map((item, i) => (
                <Card key={i} className="p-6 text-center">
                  <div className="text-5xl mb-3">{item.place}</div>
                  <p className="font-bold mb-1">{item.label}</p>
                  <p className="text-2xl font-bold text-blue-600">{item.prize}</p>
                </Card>
              ))}
            </div>
            <Card className="p-6">
              <div className="space-y-3">
                {['@DomainKing', '@TechPro', '@CryptoQueen'].map((user, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <span className="font-bold text-lg">#{i + 1}</span>
                    <span className="flex-1 font-medium">{user}</span>
                    <span className="font-bold text-blue-600">
                      {formatPrice([247000, 189000, 156000][i])}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center py-12">
            <div className="text-8xl mb-8">🎉</div>
            <h2 className="text-4xl font-bold mb-4">You're All Set!</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              You've learned the basics of DomainFlip. Ready to start trading?
            </p>
            <div className="max-w-md mx-auto space-y-4">
              <Card className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-3xl">✅</span>
                  <span className="font-bold">Marketplace Basics</span>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-3xl">✅</span>
                  <span className="font-bold">Domain Analysis</span>
                </div>
                <div className="flex items-center gap-4 mb-4">
                  <span className="text-3xl">✅</span>
                  <span className="font-bold">Trading Methods</span>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-3xl">✅</span>
                  <span className="font-bold">Portfolio Management</span>
                </div>
              </Card>
              <Button
                fullWidth
                size="lg"
                onClick={() => navigate('/marketplace')}
              >
                Start Trading Now
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (showCelebration) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center animate-bounce">
          <div className="text-9xl mb-8">🎊</div>
          <h1 className="text-5xl font-bold mb-4">Demo Complete!</h1>
          <p className="text-2xl text-gray-600">Redirecting to marketplace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Bar */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold">Interactive Demo</h2>
            <button
              onClick={handleSkip}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Skip Demo →
            </button>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-xs text-gray-600">
            <span>
              Step {currentStep + 1} of {demoSteps.length}
            </span>
            <span>{Math.round(progress)}% Complete</span>
          </div>
        </div>
      </div>

      {/* Step Indicators */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between overflow-x-auto">
            {demoSteps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => setCurrentStep(index)}
                className={`flex flex-col items-center px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  index === currentStep
                    ? 'bg-blue-50 text-blue-600'
                    : completedSteps.includes(index)
                    ? 'text-green-600'
                    : 'text-gray-400'
                }`}
              >
                <span className="text-2xl mb-1">
                  {completedSteps.includes(index) ? '✅' : index + 1}
                </span>
                <span className="text-xs">{step.id}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Demo Area */}
          <div className="lg:col-span-3">
            <Card className="p-8 min-h-[600px]">
              <DemoStepContent />
            </Card>
          </div>

          {/* Sidebar - Instructions */}
          <div className="space-y-6">
            <Card className="p-6 bg-gradient-to-br from-blue-50 to-purple-50">
              <h3 className="font-bold mb-2">{currentStepData.title}</h3>
              <p className="text-sm text-gray-600 mb-4">
                {currentStepData.description}
              </p>
            </Card>

            <Card className="p-6">
              <h3 className="font-bold mb-4">💡 Tips</h3>
              <ul className="space-y-2 text-sm">
                {currentStepData.tips.map((tip, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-blue-600">•</span>
                    <span className="text-gray-600">{tip}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6">
              <h3 className="font-bold mb-4">📊 Progress</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Steps Completed:</span>
                  <span className="font-bold">
                    {completedSteps.length}/{demoSteps.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Current Step:</span>
                  <span className="font-bold">{currentStep + 1}</span>
                </div>
              </div>
            </Card>

            {/* Navigation Buttons */}
            <div className="space-y-2">
              <Button
                fullWidth
                size="lg"
                onClick={handleNext}
              >
                {currentStep === demoSteps.length - 1 ? 'Finish Demo' : 'Next Step'} →
              </Button>
              {currentStep > 0 && (
                <Button
                  fullWidth
                  variant="outline"
                  onClick={handlePrevious}
                >
                  ← Previous Step
                </Button>
              )}
              <Button
                fullWidth
                variant="ghost"
                onClick={handleSkip}
                size="sm"
              >
                Skip to Marketplace
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoPage;
